/**
 * @fileoverview Ops console — the operator's own account. Shows who they're
 * signed in as and rotates their password (POST /ops/auth/change-password),
 * which invalidates every other session they hold and re-mints the current one.
 */

import { useContext, useState } from "react";
import { toast } from "sonner";
import { OpsAuthContext } from "@/App";
import opsApi, { getOpsErrorMessage } from "@/lib/opsApi";
import { Card, SectionHeader, Button, Avatar } from "@/components/studio/primitives";
import ThemeToggle from "@/components/ThemeToggle";

const field =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";
const label = "mb-1.5 block font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]";

export default function OpsAccountPage() {
  const { admin } = useContext(OpsAuthContext) || {};
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changing, setChanging] = useState(false);

  const name = `${admin?.first_name || ""} ${admin?.last_name || ""}`.trim() || admin?.email || "Operator";

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setChanging(true);
    try {
      await opsApi.post("/ops/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Couldn't change password"));
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Account</h1>

      <Card className="mb-6">
        <SectionHeader>Signed in as</SectionHeader>
        <div className="flex items-center gap-3">
          <Avatar name={name} />
          <div className="min-w-0">
            <div className="truncate font-sans text-[15px] text-[var(--text-primary)]" data-testid="ops-account-name">
              {name}
            </div>
            <div className="mt-0.5 truncate font-sans text-[13px] text-[var(--text-tertiary)]">{admin?.email}</div>
          </div>
        </div>
      </Card>

      <Card className="mb-6">
        <SectionHeader>Change your password</SectionHeader>
        <p className="mb-5 font-sans text-[13px] text-[var(--text-tertiary)]">
          Changing your password signs you out everywhere else. You'll stay signed in here.
        </p>
        <form onSubmit={changePassword} className="flex max-w-sm flex-col gap-4">
          <div>
            <label className={label} htmlFor="ops-pw-current">
              Current password
            </label>
            <input
              id="ops-pw-current"
              type="password"
              value={pw.current}
              onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
              data-testid="ops-pw-current"
              className={field}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className={label} htmlFor="ops-pw-new">
              New password
            </label>
            <input
              id="ops-pw-new"
              type="password"
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
              data-testid="ops-pw-new"
              className={field}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={label} htmlFor="ops-pw-confirm">
              Confirm new password
            </label>
            <input
              id="ops-pw-confirm"
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              data-testid="ops-pw-confirm"
              className={field}
              autoComplete="new-password"
            />
          </div>
          <div>
            <Button type="submit" disabled={changing} icon={changing ? "loader" : "check"} data-testid="ops-pw-submit">
              {changing ? "Saving…" : "Change password"}
            </Button>
          </div>
        </form>
      </Card>

      <Card data-testid="appearance-card">
        <SectionHeader>Appearance</SectionHeader>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="font-sans text-[13px] text-[var(--text-tertiary)]">
            Light is the default. Dark suits a dim room. Saved on this device.
          </p>
          <ThemeToggle />
        </div>
      </Card>
    </div>
  );
}
